import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Save, Loader2, Clock, Send, FileText } from 'lucide-react';
import api from '../../utils/api';
import RichTextEditor from '../../components/RichTextEditor';
import ImageUploadField from '../../components/ImageUploadField';
import SeoPanel from '../../components/SeoPanel';
import { analyseSeo } from '../../utils/seoAnalysis';

const API = process.env.REACT_APP_API_URL || '';

function slugify(str) {
  return str.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').replace(/--+/g, '-').trim();
}

// The publish time is edited in the browser's own time zone (a datetime-local field) and sent to
// the API as an exact instant, so a post scheduled for 9 am in Sydney goes live at 9 am in Sydney.
const pad = (n) => String(n).padStart(2, '0');
function toLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
const fromLocalInput = (v) => (v ? new Date(v).toISOString() : null);
const fmtWhen = (v) => new Date(v).toLocaleString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', hour: 'numeric', minute: '2-digit' });
const zoneName = (() => {
  try { return new Intl.DateTimeFormat('en-AU', { timeZoneName: 'short' }).formatToParts(new Date()).find((p) => p.type === 'timeZoneName')?.value || ''; } catch { return ''; }
})();
// Tomorrow at 9 am, local time: a sensible first suggestion when someone picks "Schedule".
function tomorrowNine() {
  const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(9, 0, 0, 0);
  return toLocalInput(d.toISOString());
}
const MODES = [
  { id: 'draft', label: 'Draft', icon: FileText },
  { id: 'published', label: 'Publish', icon: Send },
  { id: 'scheduled', label: 'Schedule', icon: Clock },
];

export default function BlogPostForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [savedStatus, setSavedStatus] = useState('draft');   // what is stored now, for the button and banner

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [categories, setCategories] = useState([]);
  const [categoriesError, setCategoriesError] = useState(false);
  const [form, setForm] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    featured_image: '',
    author: 'Rostery Team',
    category: 'General',
    tags: '',
    status: 'draft',
    published_at: '',
    meta_title: '',
    meta_description: '',
    // Rank-Math-style fields. Defaults match the DB defaults so a new post behaves the
    // same as every existing one: indexable, followable, no overrides.
    focus_keyword: '',
    secondary_keywords: '',
    canonical_url: '',
    robots_index: true,
    robots_follow: true,
    og_title: '',
    og_description: '',
    og_image: '',
    twitter_title: '',
    twitter_description: '',
    twitter_image: '',
    schema_type: 'BlogPosting',
    breadcrumb_title: '',
    is_pillar: false,
  });

  useEffect(() => {
    async function loadCategories() {
      try {
        const res = await fetch(`${API}/api/marketing/blog-categories`);
        if (!res.ok) throw new Error('Failed');
        const data = await res.json();
        const list = data.categories || data || [];
        if (!Array.isArray(list) || list.length === 0) throw new Error('Empty');
        setCategories(list);
      } catch {
        setCategoriesError(true);
      }
    }
    loadCategories();
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    async function load() {
      try {
        // Load the single record. This used to pull the whole list and search it by id —
        // but the list endpoint does not select `content`, so the form opened with an empty
        // body and saving wrote that emptiness back, silently erasing the post. The API now
        // accepts an id here and returns the full row.
        let post = null;
        try { post = (await api.get(`/marketing/blog-posts/${id}`)).data; } catch { post = null; }
        if (!post || !post.id) { toast.error('Post not found'); navigate('/website/blog'); return; }
        setForm({
          title: post.title || '',
          slug: post.slug || '',
          excerpt: post.excerpt || '',
          content: post.content || '',
          featured_image: post.featured_image || '',
          author: post.author || 'Rostery Team',
          category: post.category || 'General',
          tags: (post.tags || []).join(', '),
          status: post.status || 'draft',
          published_at: toLocalInput(post.published_at),
          meta_title: post.meta_title || '',
          meta_description: post.meta_description || '',
          focus_keyword: post.focus_keyword || '',
          secondary_keywords: (post.secondary_keywords || []).join(', '),
          canonical_url: post.canonical_url || '',
          // Explicit !== false, so a NULL from a row created before these columns existed
          // reads as "indexable" rather than silently noindexing an existing post.
          robots_index: post.robots_index !== false,
          robots_follow: post.robots_follow !== false,
          og_title: post.og_title || '',
          og_description: post.og_description || '',
          og_image: post.og_image || '',
          twitter_title: post.twitter_title || '',
          twitter_description: post.twitter_description || '',
          twitter_image: post.twitter_image || '',
          schema_type: post.schema_type || 'BlogPosting',
          breadcrumb_title: post.breadcrumb_title || '',
          is_pillar: !!post.is_pillar,
        });
        setSavedStatus(post.status || 'draft');
      } catch {
        toast.error('Failed to load post');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [id]);

  function handleTitleChange(e) {
    const title = e.target.value;
    setForm(f => ({ ...f, title, slug: isEdit ? f.slug : slugify(title) }));
  }

  function set(field) {
    return e => setForm(f => ({ ...f, [field]: e.target.value }));
  }

  function chooseMode(mode) {
    setForm(f => ({
      ...f,
      status: mode,
      // Picking "Schedule" with no time yet suggests tomorrow at 9 am; the editor can change it.
      // Picking "Publish" on a post that was scheduled for later clears that time, so "Publish now"
      // really publishes now (the API keeps a publish with a future time as a schedule). A later
      // date typed in afterwards still schedules it.
      published_at: mode === 'scheduled' && (!f.published_at || new Date(f.published_at) <= new Date()) ? tomorrowNine()
        : mode === 'published' && f.published_at && new Date(f.published_at) > new Date() ? ''
          : f.published_at,
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.title || !form.slug) { toast.error('Title and slug are required'); return; }
    if (form.status === 'scheduled') {
      if (!form.published_at) { toast.error('Choose when the post should go live.'); return; }
      if (new Date(form.published_at) <= new Date()) { toast.error('Choose a time in the future, or publish it now.'); return; }
    }
    setSaving(true);
    try {
      const secondary = form.secondary_keywords
        ? form.secondary_keywords.split(',').map(t => t.trim()).filter(Boolean) : [];
      // Score is computed here and stored, so the post list can show it without
      // re-analysing every row in the browser.
      const analysis = analyseSeo({
        title: form.title,
        metaTitle: form.meta_title,
        metaDescription: form.meta_description,
        slug: form.slug,
        content: form.content,
        excerpt: form.excerpt,
        focusKeyword: form.focus_keyword,
        secondaryKeywords: secondary,
        featuredImage: form.featured_image,
      });
      const body = {
        ...form,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        secondary_keywords: secondary,
        published_at: fromLocalInput(form.published_at),
        seo_score: analysis.score,
      };
      const { data: saved } = isEdit
        ? await api.put(`/marketing/blog-posts/${id}`, body)
        : await api.post('/marketing/blog-posts', body);
      // Say what actually happened: a "Publish" with a later date is stored as a schedule.
      if (saved.status === 'scheduled') toast.success(`Scheduled. It goes live ${fmtWhen(saved.published_at)}.`);
      else if (saved.status === 'published') toast.success(savedStatus === 'published' ? 'Post updated' : 'Post published');
      else toast.success('Draft saved');
      navigate('/website/blog');
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Could not save the post');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/website/blog')} className="p-2 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors">
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{isEdit ? 'Edit Post' : 'New Blog Post'}</h1>
          <p className="text-slate-500 text-sm mt-0.5">Fill in the details below to {isEdit ? 'update' : 'create'} your post</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-5">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
              <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Content</h2>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Title *</label>
                <input type="text" value={form.title} onChange={handleTitleChange} required
                  className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                  placeholder="e.g. How to Prepare for an NDIS Audit" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Slug *</label>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400">/blog/</span>
                  <input type="text" value={form.slug} onChange={set('slug')} required
                    className="flex-1 px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                    placeholder="how-to-prepare-ndis-audit" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Excerpt</label>
                <textarea value={form.excerpt} onChange={set('excerpt')} rows={3}
                  className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30 resize-none"
                  placeholder="A short summary shown in blog listing..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Content</label>
                <RichTextEditor
                  value={form.content}
                  onChange={html => setForm(f => ({ ...f, content: html }))}
                  placeholder="Start writing your blog post, or click 'Write with AI' to generate content automatically..."
                  minHeight={380}
                />
              </div>
            </div>

            {/* SEO — full analysis panel replaces the two bare meta fields that were here. */}
            <SeoPanel
              kind="blog"
              urlPrefix="/blog/"
              title={form.title}
              content={form.content}
              excerpt={form.excerpt}
              featuredImage={form.featured_image}
              value={form}
              onChange={next => setForm(next)}
            />
          </div>

          {/* Sidebar options */}
          <div className="space-y-5">
            {/* Publish */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
              <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Publish</h2>
              {savedStatus === 'scheduled' && form.status === 'scheduled' && form.published_at && (
                <p className="flex items-start gap-2 rounded-xl bg-indigo-50 px-3 py-2.5 text-[13px] text-indigo-800">
                  <Clock size={14} className="mt-0.5 shrink-0" />
                  <span>Scheduled. Goes live {fmtWhen(fromLocalInput(form.published_at))}.</span>
                </p>
              )}
              <div role="radiogroup" aria-label="Publishing" className="grid grid-cols-3 gap-1 rounded-xl bg-slate-100 p-1">
                {MODES.map(({ id: m, label, icon: Icon }) => (
                  <button key={m} type="button" role="radio" aria-checked={form.status === m} onClick={() => chooseMode(m)}
                    className={`flex items-center justify-center gap-1.5 rounded-lg px-2 py-2 text-[13px] font-semibold transition-colors ${form.status === m ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                    <Icon size={13} />{label}
                  </button>
                ))}
              </div>
              {form.status === 'scheduled' && (
                <div>
                  <label htmlFor="publish-at" className="block text-sm font-medium text-slate-700 mb-1.5">Goes live on</label>
                  <input id="publish-at" type="datetime-local" value={form.published_at} min={toLocalInput(new Date().toISOString())} onChange={set('published_at')}
                    className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30" />
                  <p className="mt-1.5 text-xs text-slate-500">Your time{zoneName ? ` (${zoneName})` : ''}. The post appears on the blog within a minute of this time, and stays hidden until then.</p>
                </div>
              )}
              {form.status === 'published' && (
                <div>
                  <label htmlFor="published-on" className="block text-sm font-medium text-slate-700 mb-1.5">Published on</label>
                  <input id="published-on" type="datetime-local" value={form.published_at} onChange={set('published_at')}
                    className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30" />
                  <p className="mt-1.5 text-xs text-slate-500">Leave empty to publish now. A later date schedules the post instead.</p>
                </div>
              )}
              {form.status === 'draft' && (
                <p className="text-xs text-slate-500">Only you can see a draft. Choose Publish or Schedule when it is ready.</p>
              )}
              <button type="submit" disabled={saving}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-xl text-sm font-semibold hover:bg-primary-700 transition-colors disabled:opacity-60">
                {saving ? <Loader2 size={15} className="animate-spin" /> : form.status === 'scheduled' ? <Clock size={15} /> : <Save size={15} />}
                {saving ? 'Saving' : form.status === 'scheduled' ? (savedStatus === 'scheduled' ? 'Update schedule' : 'Schedule post')
                  : form.status === 'published' ? (savedStatus === 'published' ? 'Update post' : 'Publish now') : 'Save draft'}
              </button>
            </div>

            {/* Details */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
              <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Details</h2>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Category</label>
                {!categoriesError && categories.length > 0 ? (
                  <select value={form.category} onChange={set('category')}
                    className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30">
                    <option value="">Select category…</option>
                    {/* If the current value doesn't match any fetched category, still show it */}
                    {form.category && !categories.some(c => c.name === form.category || c.slug === form.category) && (
                      <option value={form.category}>{form.category}</option>
                    )}
                    {categories.map(c => (
                      <option key={c.id} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                ) : (
                  <input type="text" value={form.category} onChange={set('category')}
                    className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                    placeholder="e.g. Compliance, NDIS, Operations" />
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Author</label>
                <input type="text" value={form.author} onChange={set('author')}
                  className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Tags (comma-separated)</label>
                <input type="text" value={form.tags} onChange={set('tags')}
                  className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                  placeholder="NDIS, Compliance, Audit" />
              </div>
              <ImageUploadField
                label="Featured Image"
                value={form.featured_image}
                onChange={(url) => setForm(f => ({ ...f, featured_image: url }))}
                hint="Shown in blog listing and at the top of the post"
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
